import type { RelationshipPack } from '../../../lib/diagram/relationship-manifest.types';

/**
 * Edge `edgeType` values below MUST be members of the rich
 * `NetworkEdgeType` union in `../NetworkProfile.ts` — that's the vocabulary
 * the canvas, view profiles, and `compassGraphToImportResult.ts` switch on.
 *
 * (Note: there is a separate, narrower `NetworkEdgeType` in
 * `src/lib/graph/types.ts` used by the hydrator for cross-domain federation
 * interop. The hydrator translates between the two vocabularies; pack values
 * here belong to the canvas/Compass layer, not the federation layer.)
 */
export const NetworkRelationshipPack: RelationshipPack = {
  meta: {
    id: 'network-relationships',
    name: 'Network Infrastructure Relationships',
    version: '1.0.0',
  },
  diagramType: 'network',
  categories: [
    { id: 'containment', label: 'Containment', order: 1 },
    { id: 'network_access', label: 'Network Access', order: 2 },
    { id: 'identity', label: 'Identity', order: 3 },
    { id: 'data_flow', label: 'Data Flow', order: 4 },
    { id: 'routing', label: 'Routing', order: 5 },
    { id: 'governance', label: 'Governance', order: 6 },
  ],
  subtypes: [
    {
      id: 'vpc_contains_subnet',
      categoryId: 'containment',
      label: 'VPC contains Subnet',
      edgeType: 'contained_in',
      ontology: {
        predicate: 'contains',
        status: 'profile_only',
      },
      defaultEdgeProps: { color: '#6b7280', width: 1, dashed: false },
    },
    {
      id: 'subnet_contains_resource',
      categoryId: 'containment',
      label: 'Subnet contains Resource',
      edgeType: 'contained_in',
      ontology: {
        predicate: 'contains',
        status: 'profile_only',
      },
      defaultEdgeProps: { color: '#6b7280', width: 1, dashed: false },
    },
    {
      id: 'sg_allows_inbound',
      categoryId: 'network_access',
      label: 'SG allows inbound',
      edgeType: 'allows_traffic_to',
      ontology: {
        predicate: 'exposes',
        status: 'profile_only',
        facets: ['governance', 'dependency'],
      },
      defaultEdgeProps: {
        color: '#22c55e',
        width: 2,
        dashed: false,
        endArrow: { type: 'triangle', size: 8 },
      },
    },
    {
      id: 'sg_allows_outbound',
      categoryId: 'network_access',
      label: 'SG allows outbound',
      edgeType: 'allows_traffic_to',
      ontology: {
        predicate: 'connects_to',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultEdgeProps: {
        color: '#3b82f6',
        width: 2,
        dashed: true,
        endArrow: { type: 'triangle', size: 8 },
      },
    },
    {
      id: 'vpc_peers_with',
      categoryId: 'network_access',
      label: 'VPC Peering',
      edgeType: 'allows_traffic_to',
      ontology: {
        predicate: 'connects_to',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultEdgeProps: {
        color: '#8b5cf6',
        width: 2,
        dashed: true,
        endArrow: { type: 'none', size: 6 },
      },
    },
    {
      id: 'assumes_role',
      categoryId: 'identity',
      label: 'Assumes Role',
      edgeType: 'assumes_identity',
      ontology: {
        predicate: 'uses_identity',
        status: 'profile_only',
        facets: ['identity', 'dependency'],
      },
      defaultEdgeProps: {
        color: '#f59e0b',
        width: 2,
        dashed: true,
        endArrow: { type: 'triangle', size: 8 },
      },
    },
    {
      id: 'role_trusts',
      categoryId: 'identity',
      label: 'Trust Relationship',
      edgeType: 'assumes_identity',
      ontology: {
        predicate: 'uses_identity',
        status: 'profile_only',
        facets: ['identity'],
        qualifierKeys: ['trustDirection'],
      },
      defaultEdgeProps: {
        color: '#f59e0b',
        width: 1,
        dashed: true,
      },
    },
    {
      id: 'reads_from',
      categoryId: 'data_flow',
      label: 'Reads from',
      edgeType: 'reads_from',
      ontology: {
        predicate: 'data_flow',
        status: 'profile_only',
        facets: ['dependency'],
        qualifierKeys: ['operation'],
      },
      defaultEdgeProps: {
        color: '#06b6d4',
        width: 2,
        dashed: false,
        endArrow: { type: 'triangle', size: 8 },
      },
    },
    {
      id: 'writes_to',
      categoryId: 'data_flow',
      label: 'Writes to',
      edgeType: 'writes_to',
      ontology: {
        predicate: 'data_flow',
        status: 'profile_only',
        facets: ['dependency'],
        qualifierKeys: ['operation'],
      },
      defaultEdgeProps: {
        color: '#ec4899',
        width: 2,
        dashed: false,
        endArrow: { type: 'triangle', size: 8 },
      },
    },
    {
      id: 'routes_to_target',
      categoryId: 'routing',
      label: 'Routes to Target',
      edgeType: 'load_balances_to',
      ontology: {
        predicate: 'connects_to',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultEdgeProps: {
        color: '#10b981',
        width: 2,
        dashed: false,
        endArrow: { type: 'triangle', size: 8 },
      },
    },
    {
      id: 'encrypted_by',
      categoryId: 'governance',
      label: 'Encrypted by KMS Key',
      edgeType: 'encrypted_by',
      ontology: {
        predicate: 'protects',
        status: 'profile_only',
        facets: ['governance'],
      },
      defaultEdgeProps: {
        color: '#a855f7',
        width: 1,
        dashed: true,
      },
    },
    {
      id: 'logs_to',
      categoryId: 'governance',
      label: 'Logs to',
      edgeType: 'logs_to',
      ontology: {
        predicate: 'supports_fact',
        status: 'exchange_only',
        facets: ['evidence_support', 'provenance'],
      },
      defaultEdgeProps: {
        color: '#6b7280',
        width: 1,
        dashed: true,
      },
    },
  ],
};
